<script setup lang="ts">
import { computed, watch, onMounted, ref } from 'vue'
import type { SurveyQuestion } from '@/types/surveyTypes'
import { Field, ErrorMessage } from 'vee-validate'
import { useSurvey, AnswerTarget } from '@/composables/onlineSurvey/useSurvey'
import { useI18n } from '@/i18n'

const { setAnswer, answers } = useSurvey()
const { t } = useI18n()

const props = defineProps<{
  question: SurveyQuestion
  answerTarget?: AnswerTarget
}>()

const selectedValue = ref<string>('')
const failedImages = ref<Set<string>>(new Set())

// Helper-Funktion um die richtige Datenquelle zu bekommen
const getDataSource = () => {
  return answers
}

const emit = defineEmits<{
  (e: 'answerSelected'): void
}>()

const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  selectedValue.value = target.value
  setAnswer(props.question.id, props.question.label, target.value, props.answerTarget)
  emit('answerSelected')
}

const handleImageClick = (optionValue: string) => {
  selectedValue.value = optionValue
  setAnswer(props.question.id, props.question.label, optionValue, props.answerTarget)
  emit('answerSelected')
}

const isSelected = (optionValue: string) => selectedValue.value === optionValue

const handleImageError = (optionValue: string) => {
  failedImages.value.add(optionValue)
}

const fieldName = computed(() => props.question.id)

// Check if any option has an image_url
const hasImages = computed(() => {
  return props.question.options?.some((option) => option.image_url)
})

const initializeValue = () => {
  const dataSource = getDataSource()
  const existingAnswer = dataSource.value.find((a) => a.id === props.question.id)
  if (existingAnswer !== undefined) {
    selectedValue.value = existingAnswer.answer || ''
    return existingAnswer.answer
  }
  selectedValue.value = ''
  return undefined
}

onMounted(() => {
  initializeValue()
})

watch(
  () => getDataSource().value.find((a) => a.id === props.question.id),
  () => {
    initializeValue()
  },
)
</script>

<template>
  <div class="field">
    <Field :name="fieldName" :model-value="selectedValue" class="is-hidden" />

    <!-- Bildbasierte Auswahl -->
    <div v-if="hasImages" class="columns is-multiline is-centered px-2">
      <div
        v-for="option in props.question.options"
        :key="option.value"
        class="column is-4-desktop is-6-tablet is-12-mobile is-flex p-2"
      >
        <div
          class="image-choice-item"
          :class="{
            'is-selected': isSelected(option.value),
          }"
          @click="handleImageClick(option.value)"
        >
          <!-- Bild Container -->
          <div class="image-container" v-if="!failedImages.has(option.value) && option.image_url">
            <img
              :src="option.image_url"
              :alt="option.image_alt || option.label"
              class="choice-image"
              @error="handleImageError(option.value)"
            />

            <!-- Auswahl-Indikator -->
            <Transition name="pop">
              <div v-if="isSelected(option.value)" class="selection-indicator">
                <span class="icon is-small">✓</span>
              </div>
            </Transition>
          </div>

          <!-- Label unter dem Bild -->
          <p class="choice-label">{{ option.label }}</p>
        </div>
      </div>
    </div>

    <!-- Fallback: Standard Dropdown -->
    <div v-else class="control">
      <div class="select is-fullwidth">
        <Field :name="fieldName" :value="initializeValue()" v-slot="{ field, errorMessage }">
          <select
            :id="props.question.id"
            v-bind="field"
            :class="{ 'is-danger': errorMessage }"
            @change="handleChange"
          >
            <option value="">
              {{ props.question.placeholder || t('selectPlaceholder') }}
            </option>
            <option
              v-for="option in props.question.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </Field>
      </div>
    </div>

    <ErrorMessage :name="fieldName" class="help is-danger" />
  </div>
</template>

<style>
@import '../../../assets/main.css';
/* Grid Layout für Bild-Auswahl */
.image-choice-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 1rem;
  border-radius: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: var(--bulma-scheme-main, #ffffff);
  border: 2px solid var(--bulma-border-weak, #f5f5f5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  height: 100%;
  width: 100%;
}

.image-choice-item:hover {
  background-color: var(--bulma-background, #f2f2f2);
  border-color: var(--primary-color);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.image-choice-item.is-selected {
  background-color: color-mix(in srgb, var(--primary-color) 5%, var(--bulma-scheme-main));
  border-color: color-mix(in srgb, var(--primary-color) 40%, transparent);
  box-shadow: 0 4px 16px color-mix(in srgb, var(--primary-color) 10%, transparent);
}

/* Bild Container */
.image-container {
  position: relative;
  width: 100%;
  max-width: 180px;
  aspect-ratio: 1 / 1;
  margin-bottom: 0.75rem;
}

.choice-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 4px solid var(--bulma-border-weak, #f5f5f5);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.image-choice-item:hover .choice-image {
  transform: scale(1.08);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.image-choice-item.is-selected .choice-image {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--primary-color) 20%, transparent);
}

/* Auswahl-Indikator */
.selection-indicator {
  position: absolute;
  top: 0;
  right: 0;
  width: 32px;
  height: 32px;
  background-color: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 3px solid white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

/* Transitions */
.pop-enter-active {
  animation: pop-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.pop-leave-active {
  animation: pop-in 0.2s reverse ease-in;
}

@keyframes pop-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  80% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Label unter dem Bild */
.choice-label {
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  color: var(--bulma-text, #4a4a4a);
  line-height: 1.2;
  margin: 0.5rem;
  transition: color 0.3s ease;
}

.image-choice-item.is-selected .choice-label {
  color: var(--primary-color);
}

/* Platzhalter für fehlgeschlagene Bilder */
.image-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: var(--bulma-background, #f5f5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--bulma-border-weak, #e0e0e0);
}

.image-placeholder .icon {
  font-size: 3rem;
  opacity: 0.3;
}

.stepper-item-question-label {
  color: var(--primary-color);
}
</style>
